import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase"; // adapte le chemin si besoin
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";

const MAX_LOGO_MB = 2;
const MAX_BANNER_MB = 4;

const baseBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 16px",
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 800,
  border: "none",
  cursor: "pointer",
};

function bytesToMB(b) {
  return (b / 1024 / 1024).toFixed(2);
}
function safeTrim(v) {
  return String(v ?? "").trim();
}

export default function VendorProfileEdit() {
  const auth = useMemo(() => getAuth(), []);
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [form, setForm] = useState({
    shopName: "",
    phone: "",
    city: "",
    categories: "",
    logoUrl: "",
    bannerUrl: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // load current profile
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      setInfo(null);

      try {
        if (!user?.uid) {
          setError("Vous devez être connecté(e) pour modifier votre profil.");
          return;
        }

        const refUser = doc(db, "users", user.uid);
        const snap = await getDoc(refUser);
        if (!snap.exists()) {
          setError("Profil introuvable dans la collection users.");
          return;
        }

        const data = snap.data() || {};
        if (data.role && data.role !== "vendor") {
          setError("Ce compte n'est pas un compte vendeur.");
          return;
        }

        if (!mounted) return;
        setForm({
          shopName: data.shopName || data.displayName || "",
          phone: data.phone || "",
          city: data.city || "",
          categories: data.categories || "",
          logoUrl: data.logoUrl || "",
          bannerUrl: data.bannerUrl || "",
        });
        setLogoPreview(data.logoUrl || null);
        setBannerPreview(data.bannerUrl || null);
      } catch (e) {
        console.error(e);
        if (mounted) setError("Erreur lors du chargement du profil.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [user?.uid]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const validateFile = (file, type) => {
    if (!file) return null;

    const isImage = file.type?.startsWith("image/");
    if (!isImage) return `Le fichier ${type} doit être une image (jpg/png/webp).`;

    const max = type === "logo" ? MAX_LOGO_MB : MAX_BANNER_MB;
    const sizeMb = Number(bytesToMB(file.size));
    if (sizeMb > max) return `Le ${type} est trop lourd (${sizeMb}MB). Max: ${max}MB.`;

    return null;
  };

  const onPickLogo = (file) => {
    const msg = validateFile(file, "logo");
    if (msg) return setError(msg);

    setError(null);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const onPickBanner = (file) => {
    const msg = validateFile(file, "banner");
    if (msg) return setError(msg);

    setError(null);
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const uploadIfNeeded = async (file, path) => {
    if (!file) return null;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setInfo(null);

    try {
      if (!user?.uid) throw new Error("Not signed in");

      // validations
      const shopName = safeTrim(form.shopName);
      const phone = safeTrim(form.phone);
      const city = safeTrim(form.city);
      const categories = safeTrim(form.categories);

      if (!shopName) {
        setError("Le nom de la boutique est obligatoire.");
        return;
      }
      if (!phone) {
        setError("Le téléphone est obligatoire.");
        return;
      }
      if (!city) {
        setError("La ville/pays est obligatoire.");
        return;
      }

      // upload files if picked
      const ts = Date.now();
      const basePath = `vendors/${user.uid}`;
      const logoUrl = await uploadIfNeeded(
        logoFile,
        `${basePath}/logo-${ts}-${logoFile?.name || "logo"}`
      );
      const bannerUrl = await uploadIfNeeded(
        bannerFile,
        `${basePath}/banner-${ts}-${bannerFile?.name || "banner"}`
      );

      // update Firestore
      const refUser = doc(db, "users", user.uid);
      await updateDoc(refUser, {
        shopName,
        displayName: shopName, // optionnel, pratique
        phone,
        city,
        categories: categories || null,
        ...(logoUrl ? { logoUrl } : {}),
        ...(bannerUrl ? { bannerUrl } : {}),
        updatedAt: serverTimestamp(),
      });

      setInfo("Profil mis à jour ✅");
      setLogoFile(null);
      setBannerFile(null);

      // si upload -> preview devient URL finale
      if (logoUrl) setLogoPreview(logoUrl);
      if (bannerUrl) setBannerPreview(bannerUrl);
    } catch (err) {
      console.error(err);
      setError("Erreur lors de l’enregistrement. Réessaie.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: 18 }}>
        <div style={{ maxWidth: 980, margin: "0 auto", padding: 18, background: "#fff", borderRadius: 18 }}>
          <p style={{ color: "#64748b", fontWeight: 900 }}>Chargement du profil…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "18px 12px" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg,#0b1226,#111827)",
            borderRadius: 22,
            padding: 18,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 18,
                background: "#fff",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontWeight: 900, color: "#111827", fontSize: 12 }}>LOGO</span>
              )}
            </div>

            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{form.shopName || "Ma boutique"}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{user?.email || ""}</div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              padding: "10px 12px",
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 800,
              marginBottom: 10,
            }}
          >
            {error}
          </div>
        )}
        {info && (
          <div
            style={{
              background: "#ecfdf3",
              border: "1px solid #bbf7d0",
              color: "#166534",
              padding: "10px 12px",
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 800,
              marginBottom: 10,
            }}
          >
            {info}
          </div>
        )}

        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 22,
            boxShadow: "0 10px 26px rgba(15,23,42,0.08)",
            overflow: "hidden",
          }}
        >
          {/* Banner preview */}
          <div style={{ position: "relative", background: "#0f172a" }}>
            <div style={{ aspectRatio: "16/5", minHeight: 160 }}>
              {bannerPreview ? (
                <img
                  src={bannerPreview}
                  alt="banner"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "rgba(255,255,255,.88)",
                    fontWeight: 900,
                  }}
                >
                  Ajoutez une bannière
                </div>
              )}
            </div>

            <div style={{ position: "absolute", left: 16, bottom: 12, display: "flex", gap: 8 }}>
              <button
                type="button"
                style={{ ...baseBtn, background: "#fff", color: "#111827" }}
                onClick={() => bannerInputRef.current?.click()}
              >
                Changer bannière
              </button>
              <button
                type="button"
                style={{ ...baseBtn, background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.18)" }}
                onClick={() => logoInputRef.current?.click()}
              >
                Changer logo
              </button>
            </div>

            {/* hidden inputs */}
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onPickBanner(e.target.files?.[0] || null)}
            />
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onPickLogo(e.target.files?.[0] || null)}
            />
          </div>

          {/* Form */}
          <form onSubmit={onSave} style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field
                label="Nom de la boutique *"
                name="shopName"
                value={form.shopName}
                onChange={onChange}
                placeholder="Ex : GoShop Beauté"
              />
              <Field
                label="Téléphone (WhatsApp) *"
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="Ex : +228 90 00 00 00"
              />
              <Field
                label="Ville / Pays *"
                name="city"
                value={form.city}
                onChange={onChange}
                placeholder="Ex : Lomé, Togo"
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <FieldArea
                  label="Catégories (séparées par des virgules)"
                  name="categories"
                  value={form.categories}
                  onChange={onChange}
                  placeholder="Mode, Chaussures, Beauté, Accessoires…"
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 10 }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...baseBtn,
                  background: "linear-gradient(135deg,#1e17eb,#111827)",
                  color: "#fff",
                  boxShadow: "0 12px 28px rgba(15,23,42,.18)",
                  opacity: saving ? 0.8 : 1,
                }}
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>

            <p style={{ marginTop: 10, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              Astuce : Logo max {MAX_LOGO_MB}MB, Bannière max {MAX_BANNER_MB}MB.
            </p>
          </form>
        </div>

        {/* Mobile responsive */}
        <style>{`
          @media (max-width: 820px){
            form > div:first-child{
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>{label}</div>
      <input
        {...props}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: "10px 12px",
          fontSize: 13,
          outline: "none",
        }}
      />
    </div>
  );
}

function FieldArea({ label, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>{label}</div>
      <textarea
        {...props}
        rows={4}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: "10px 12px",
          fontSize: 13,
          outline: "none",
          resize: "vertical",
        }}
      />
    </div>
  );
}
